/**
 * Geometry of the app's persistent chrome — the primary controls strip and the primary navigation strip — as plain
 * numbers with no dependencies.
 *
 * These live in their own entities package because TWO trees have to agree on them and only one of
 * them may import the app: the real chrome (`PrimaryControlsStrip` + the primary navigation layout) and the native boot
 * shell that stands in for it from splash removal until a routed screen commits. The boot shell
 * cannot import `routes.nav._components.primary-controls-strip` — that package pulls in Uniwind, Reanimated,
 * expo-router, icons and auth, i.e. exactly the graph the boot contract exists to keep out of the
 * pre-hide runtime. So the shell used to carry its own hand-written copies of these numbers, they
 * drifted from the real ones, and the drift is what the eye reads as the app "jumping" the moment
 * the shell is swapped for the router. Sharing one source of truth is what removes that jump; a
 * change here must remain a change for both consumers.
 *
 * Values are logical points, verified against the rendered view hierarchy on a 390x844 device.
 */

/** Height of the primary controls strip's control row, excluding the top safe-area padding above it. */
export const PRIMARY_CONTROLS_STRIP_HEIGHT = 54;

/**
 * iOS shifts the toolbar down by this much and absorbs the same amount out of the top safe-area
 * padding, so the row's controls sit balanced against the physical notch rather than against the
 * inset's conservative edge. Net primary controls strip height is unchanged; only the split moves.
 */
export const PRIMARY_CONTROLS_STRIP_IOS_TOP_ADJUSTMENT = 8;

/**
 * Distance from the screen edge to the outer edge of a primary controls strip control.
 *
 * The row is padded `px-3` (12) and each control sits in a lane that pulls it 6 back out, so the
 * measured result is 6 on both sides. The boot shell positions against this directly.
 */
export const PRIMARY_CONTROLS_STRIP_CONTROL_EDGE_INSET = 6;

/** Edge length of the primary controls strip's circular controls (Tailwind `size-9`). */
export const PRIMARY_CONTROLS_STRIP_CONTROL_SIZE = 36;

/** Font size of a primary navigation strip label. */
export const PRIMARY_NAVIGATION_STRIP_LABEL_FONT_SIZE = 11;

/** Line height of a primary navigation strip label. Must carry an absolute unit — see the styled-components note. */
export const PRIMARY_NAVIGATION_STRIP_LABEL_LINE_HEIGHT = 14;

/** Rendered edge length of a primary navigation strip icon. */
export const PRIMARY_NAVIGATION_STRIP_ICON_SIZE = 21;

/** Top offset of the icon inside a tab button, before the label stack. */
export const PRIMARY_NAVIGATION_STRIP_ICON_TOP_OFFSET = 9;

/** Top offset of the label inside a tab button. */
export const PRIMARY_NAVIGATION_STRIP_LABEL_TOP_OFFSET = 32;

/**
 * The primary navigation destination order, left to right, with the label rendered under each icon.
 *
 * The boot shell paints these before a navigator exists, so it cannot read them from the router;
 * keeping the list here is what stops the shell's tabs drifting from the real ones. Note the
 * `Deck` tab's route is named `Decks` — the visible label is what matters for the handoff.
 */
export const PRIMARY_NAVIGATION_STRIP_LABEL_LIST = [
  "Stats",
  "Deck",
  "Learn",
  "Browse",
  "Search",
] as const;

/** The tab the native cold-start destination (`/learn/summary`) lands on. */
export const INITIAL_PRIMARY_NAVIGATION_STRIP_LABEL = "Learn";
